import type { ChannelPage } from '../channel/tabs.ts';
import type { ChannelDetails, ChannelSummary } from '../model/channel.ts';
import type { PlaylistSummary } from '../model/playlist.ts';
import type { VideoSummary } from '../model/video.ts';
import { err, ok, parseError, type ParsedList, type ParseError, type Result } from '../result.ts';
import type { ParseContext } from './context.ts';
import { parseImageList } from './image.ts';
import { parsePlaylistSummary } from './playlist.ts';
import {
  asArray,
  asBoolean,
  asNonEmptyString,
  asNumber,
  asObject,
  asString,
  asStringArray,
  field,
} from './raw.ts';
import { parseDescriptionNodes } from './richText.ts';
import { asEpochSeconds } from './time.ts';
import { parseVideoSummary } from './video.ts';

/**
 * R4 — the `channel` arm of `/api/v1/search`. Total: the only thing that fails the parse is a
 * record with no `authorId`, because without a UCID the row is neither linkable nor hydratable.
 *
 * Everything else degrades. An empty `author` is survivable, `channelHandle` is genuinely `null`
 * for auto-generated channels, and `subCount: 0` is what the API reports for many of them — a real
 * zero and an unknown count are indistinguishable on the wire, so this does not pretend otherwise.
 */
export function parseChannelSummary(raw: unknown, ctx: ParseContext): Result<ChannelSummary, ParseError> {
  const source = asObject(raw);
  if (!source) return err(parseError('ChannelSummary', null, 'wrong-type'));

  const channelId = asNonEmptyString(field(source, 'authorId'));
  if (channelId === null) return err(parseError('ChannelSummary', 'authorId', 'missing'));

  const description = asString(field(source, 'description')) ?? '';

  return ok({
    channelId,
    name: asString(field(source, 'author')) ?? '',
    handle: asNonEmptyString(field(source, 'channelHandle')),
    channelUrl: asNonEmptyString(field(source, 'authorUrl')),
    verified: asBoolean(field(source, 'authorVerified')) ?? false,
    autoGenerated: asBoolean(field(source, 'autoGenerated')) ?? false,
    subscriberCount: asNumber(field(source, 'subCount')),
    videoCount: asNumber(field(source, 'videoCount')),
    description,
    descriptionNodes: parseDescriptionNodes(description, asString(field(source, 'descriptionHtml')) ?? ''),
    // R2 — and this is the list where the protocol-relative `//yt3.ggpht.com/…` form shows up.
    thumbnails: parseImageList(field(source, 'authorThumbnails'), ctx),
  });
}

export function parseChannelSummaryList(raw: unknown, ctx: ParseContext): ParsedList<ChannelSummary> {
  const array = asArray(raw);
  if (!array) return { items: [], dropped: [parseError('ChannelSummaryList', null, 'wrong-type')] };
  const items: ChannelSummary[] = [];
  const dropped: ParseError[] = [];
  for (const entry of array) {
    const parsed = parseChannelSummary(entry, ctx);
    if (parsed.ok) items.push(parsed.value);
    else dropped.push(parsed.error);
  }
  return { items, dropped };
}

/**
 * R4 — `GET /api/v1/channels/{ucid}`, the record §8's channel header and About tab render.
 *
 * Total, and the only fatal field is `authorId`: without a UCID the page cannot fetch its own tabs.
 * Everything else degrades, including three fields that are dead on this instance and are read as
 * absences rather than as values — see {@link ChannelDetails} for the measurements.
 *
 * `latestVideos` is deliberately not read. It is 30 rows arriving free with this response, but it
 * carries **no continuation**, and the videos tab's own endpoint answers with 60 rows *and* one. So
 * seeding the tab from here would trade paging for a saved request, which is the wrong trade for the
 * one tab a reader scrolls.
 */
export function parseChannelDetails(raw: unknown, ctx: ParseContext): Result<ChannelDetails, ParseError> {
  const source = asObject(raw);
  if (!source) return err(parseError('ChannelDetails', null, 'wrong-type'));

  const channelId = asNonEmptyString(field(source, 'authorId'));
  if (channelId === null) return err(parseError('ChannelDetails', 'authorId', 'missing'));

  const description = asString(field(source, 'description')) ?? '';

  return ok({
    channelId,
    name: asString(field(source, 'author')) ?? '',
    verified: asBoolean(field(source, 'authorVerified')) ?? false,
    autoGenerated: asBoolean(field(source, 'autoGenerated')) ?? false,
    ageGated: asBoolean(field(source, 'ageGated')) ?? false,
    // Absent means "nothing said it was restricted", which is the safe default for a *label*.
    familyFriendly: asBoolean(field(source, 'isFamilyFriendly')) ?? true,
    subscriberCount: asNumber(field(source, 'subCount')),
    // `0` on 10 of 10 channels, so this is an absence rather than a figure. `asNumber` would hand
    // over the zero and the About tab would state "0 views" about a channel with billions.
    totalViews: positiveOrNull(asNumber(field(source, 'totalViews'))),
    // Likewise `0`, and here the zero is epoch zero — `asEpochSeconds` already refuses it, which is
    // what keeps "joined January 1970" off the page.
    joinedEpochSec: asEpochSeconds(field(source, 'joined')),
    description,
    descriptionNodes: parseDescriptionNodes(description, asString(field(source, 'descriptionHtml')) ?? ''),
    // R2 both times, and both lists carry Google hosts: avatars are `yt3.googleusercontent.com` and
    // banners the same, so neither reaches a component unrewritten.
    avatars: parseImageList(field(source, 'authorThumbnails'), ctx),
    banners: parseImageList(field(source, 'authorBanners'), ctx),
    tags: asStringArray(field(source, 'tags')),
    tabs: asStringArray(field(source, 'tabs')),
  });
}

function positiveOrNull(value: number | null): number | null {
  return value === null || value <= 0 ? null : value;
}

/**
 * R4 — one page of `/videos`, `/shorts` or `/streams`.
 *
 * **These are the second family of endpoints in this build with an envelope**, after
 * `/api/v1/hashtag/{tag}`: `{"videos":[…],"continuation":"…"}`, where every other list is a bare
 * array. The envelope is also where the end-of-list signal lives — `continuation` is simply *absent*
 * on the last page (verified on a five-item shorts tab and on an empty streams tab), and it is the
 * only such signal: a **short page is not the end** (`/playlists` returned 29 rows on page 7 of 12).
 *
 * A missing or non-array `videos` is one dropped `wrong-type`, which is what lets a caller tell "this
 * tab is empty" from "this response was not a tab" — the distinction `api/feeds.ts` and
 * `api/search.ts` both act on. Worth having here because an **unknown sub-path answers `200` with
 * zero bytes** (`/api/v1/channels/{ucid}/notatab`), the captions trap in a third place, and a typo
 * must not read as an empty channel.
 */
export function parseChannelVideoPage(raw: unknown, ctx: ParseContext): ChannelPage<VideoSummary> {
  return parseChannelPage(raw, 'ChannelVideoPage', 'videos', (entry) => parseVideoSummary(entry, ctx));
}

/**
 * R4 — one page of `/playlists`. Same envelope under a different key, and its rows are
 * `PlaylistSummary`.
 *
 * Two differences from a search playlist row, both measured. `videoCount` here is genuine (69, 41, 33,
 * 29, 22… — 28 of 30 rows on one response, and the two zeros were gone on the next four), and
 * `playlistThumbnail` comes in a **third** URL form:
 * `i.ytimg.com/pl_c/{PLID}/studio_square_thumbnail.jpg?sqp=…`, whose `sqp` parameter is *required*
 * (404 without it) and *encodes the size* — 240×240 on one playlist, 426×240 on another, under the
 * same filename. `rewriteAssetUrl` grew an arm for it, and unlike a `/vi/` frame it must not be
 * retargeted: there is no second filename, and the query is the only thing that resolves the image.
 * One response carried 24 of the `/vi/` form and 6 of this one, so both arms run on every real page.
 */
export function parseChannelPlaylistPage(
  raw: unknown,
  ctx: ParseContext,
): ChannelPage<PlaylistSummary> {
  return parseChannelPage(raw, 'ChannelPlaylistPage', 'playlists', (entry) =>
    parsePlaylistSummary(entry, ctx),
  );
}

function parseChannelPage<T>(
  raw: unknown,
  entity: string,
  key: 'videos' | 'playlists',
  parseOne: (entry: unknown) => Result<T, ParseError>,
): ChannelPage<T> {
  const source = asObject(raw);
  if (!source) return { items: [], continuation: null, dropped: [parseError(entity, null, 'wrong-type')] };

  const array = asArray(field(source, key));
  if (!array) return { items: [], continuation: null, dropped: [parseError(entity, key, 'wrong-type')] };

  const items: T[] = [];
  const dropped: ParseError[] = [];
  for (const entry of array) {
    const parsed = parseOne(entry);
    if (parsed.ok) items.push(parsed.value);
    else dropped.push(parsed.error);
  }

  return {
    items,
    // An empty-string token is treated as absent: it would be sent, and a continuation the server
    // cannot read answers `500` rather than "no more".
    continuation: asNonEmptyString(field(source, 'continuation')),
    dropped,
  };
}
